import type { ErpModule } from '@/api/platform.api';
import { getAppNavConfig } from '@/config/appNavigation';
import {
  getModuleEntryByErpModule,
  MODULE_ENTRIES,
  type ModuleEntry,
} from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { ORG_ROUTE_ACCESS } from '@/config/routeAccessRegistry';
import { resolveRouteAccessRule } from '@/config/routeAccessUtils';
import { getLauncherModules, isOrgOwner } from '@/utils/moduleAccess';
import { getPostLoginPath, HR_ESS_HOME_PATH, isHrEssOnlyUser } from '@/utils/moduleScope';
import { canAccessWorkspacePath } from '@/utils/moduleNavigation';

export type PageAccessContext = {
  permissions: readonly string[];
  roleCode?: string | null;
  enabledModules: string[];
  accessibleModules?: string[];
  entryModule?: ErpModule | null;
  hasPermission?: (code: string) => boolean;
  hasFeature?: (code: string) => boolean;
};

function hasRoutePermission(
  pathname: string,
  permissions: readonly string[],
  roleCode?: string | null,
): boolean {
  if (isOrgOwner(permissions, roleCode)) return true;
  if (permissions.includes('MASTER_ADMIN')) return true;

  const rule = resolveRouteAccessRule(pathname, ORG_ROUTE_ACCESS);
  if (!rule?.view) return true;

  const required = rule.view;
  return Array.isArray(required)
    ? required.some((p) => permissions.includes(p))
    : permissions.includes(required);
}

function firstPermittedNavPath(
  entry: ModuleEntry,
  ctx: PageAccessContext,
): string | null {
  const nav = getAppNavConfig(entry.erpModule);
  if (!nav) return null;

  for (const group of nav.groups) {
    for (const item of group.items) {
      const path = item.to.split('?')[0];
      if (!canAccessWorkspacePath(path, ctx.enabledModules, [entry.erpModule], ctx.accessibleModules)) {
        continue;
      }
      if (!hasRoutePermission(path, ctx.permissions, ctx.roleCode)) {
        continue;
      }
      if (ctx.hasFeature && item.feature && !ctx.hasFeature(item.feature)) {
        continue;
      }
      return path;
    }
  }
  return null;
}

/** Resolve the best landing URL when the default dashboard is unavailable. */
export function resolveAccessibleLandingPath(
  ctx: PageAccessContext,
  preferredModule?: ErpModule | null,
): string {
  const has =
    ctx.hasPermission ??
    ((code: string) => ctx.permissions.includes('MASTER_ADMIN') || ctx.permissions.includes(code));

  if (isHrEssOnlyUser(ctx.permissions, ctx.accessibleModules, ctx.roleCode)) {
    return HR_ESS_HOME_PATH;
  }

  const launcherApps = getLauncherModules(
    ctx.enabledModules,
    ctx.permissions,
    ctx.accessibleModules,
    ctx.roleCode,
    has,
  );

  const launcherCodes = launcherApps.map((e) => e.erpModule);
  const orderedModules: ErpModule[] = [];

  if (preferredModule && launcherCodes.includes(preferredModule)) {
    orderedModules.push(preferredModule);
  }
  if (ctx.entryModule && launcherCodes.includes(ctx.entryModule)) {
    orderedModules.push(ctx.entryModule);
  }
  for (const code of launcherCodes) {
    if (!orderedModules.includes(code)) {
      orderedModules.push(code);
    }
  }

  for (const module of orderedModules) {
    const entry = getModuleEntryByErpModule(module);
    if (!entry) continue;

    const home = entry.entryPath ?? entry.homePath;
    if (
      canAccessWorkspacePath(home, ctx.enabledModules, launcherCodes, ctx.accessibleModules) &&
      hasRoutePermission(home, ctx.permissions, ctx.roleCode)
    ) {
      return home;
    }

    const navPath = firstPermittedNavPath(entry, {
      ...ctx,
      hasPermission: has,
    });
    if (navPath) return navPath;
  }

  if (ctx.entryModule) {
    return getPostLoginPath(ctx.entryModule, MODULE_HUB_PATH, {
      permissions: ctx.permissions,
      accessibleModules: ctx.accessibleModules,
      roleCode: ctx.roleCode,
    });
  }

  for (const entry of MODULE_ENTRIES) {
    const home = entry.homePath;
    if (
      canAccessWorkspacePath(home, ctx.enabledModules, launcherCodes, ctx.accessibleModules) &&
      hasRoutePermission(home, ctx.permissions, ctx.roleCode)
    ) {
      return home;
    }
  }

  return MODULE_HUB_PATH;
}
